import Swiper from 'swiper';
import { Autoplay, Navigation, Pagination } from 'swiper/modules';
import { breakpoints, swiperKeyboardNavigationHandler, swiperUpdateSlides } from './utils';

const buttonSmallClass = 'c-button-small';
const buttonSmallAppearanceDisabledClass = buttonSmallClass + '--appearance-0';

const componentJsClass = 'js-carousel';
const sliderJsClass = componentJsClass + '-slider';
const buttonPreviousJsClass = componentJsClass + '-button-previous';
const buttonNextJsClass = componentJsClass + '-button-next';
const paginationJsClass = componentJsClass + '-pagination';
const buttonPlayJsClass = componentJsClass + '-button-play';
const buttonPauseJsClass = componentJsClass + '-button-pause';

// Get all carousel components
const carouselElements = document.querySelectorAll('.' + componentJsClass) as NodeListOf<HTMLElement>;

// For each carousel component
carouselElements.forEach((carouselElement) => {
    initializeCarousel(carouselElement);
});

export function initializeCarousel (carouselElement: HTMLElement, initialSlide = 0): void {
    // Get carousel and navigation elements we need
    const sliderElement = carouselElement.querySelector('.' + sliderJsClass) as HTMLElement;
    const buttonPreviousElement = carouselElement.querySelector('.' + buttonPreviousJsClass) as HTMLElement;
    const buttonNextElement = carouselElement.querySelector('.' + buttonNextJsClass) as HTMLElement;
    const paginationElement = carouselElement.querySelector('.' + paginationJsClass) as HTMLElement;
    const buttonPlayElement = carouselElement.querySelector('.' + buttonPlayJsClass) as HTMLElement;
    const buttonPauseElement = carouselElement.querySelector('.' + buttonPauseJsClass) as HTMLElement;

    // Get options from data attribute
    const allowTouchMove = sliderElement.dataset.allowTouchMove === 'false' ? false : true;
    const autoplayDelay = Number(sliderElement.dataset.autoplayDelay);
    const loop = sliderElement.dataset.loop === 'true' ? true : false;
    const slidesPerViewMax = Number(sliderElement.dataset.slidesPerViewMax);

    let swiperBreakpoints = {};
    let slidesPerView: number;

    // Set swiper breakpoints based on slidesPerViewMax
    switch (slidesPerViewMax) {
        case 1:
            slidesPerView = 1;
            swiperBreakpoints = {};
            break;
        case 2:
            slidesPerView = 1.2;
            swiperBreakpoints = {
                [breakpoints.l]: {
                    slidesPerView: 2.2
                }
            };
            break;
        case 3:
            slidesPerView = 1.2;
            swiperBreakpoints = {
                [breakpoints.l]: {
                    slidesPerView: 2.2
                },
                [breakpoints.xxl]: {
                    slidesPerView: 3.2
                }
            };
            break;
        case 4:
            slidesPerView = 1.2;
            swiperBreakpoints = {
                [breakpoints.l]: {
                    slidesPerView: 2.2
                },
                [breakpoints.xxl]: {
                    slidesPerView: 4.2
                }
            };
            break;
    }

    // Initialize Swiper
    const swiper = new Swiper(sliderElement, {
        allowTouchMove,
        autoplay: autoplayDelay ? {
            delay: autoplayDelay
        } : false,
        breakpoints: swiperBreakpoints,
        initialSlide,
        loop,
        modules: [Autoplay, Navigation, Pagination],
        mousewheel: {
            thresholdDelta: 70
        },
        navigation: {
            disabledClass: buttonSmallAppearanceDisabledClass,
            nextEl: buttonNextElement,
            prevEl: buttonPreviousElement
        },
        pagination: {
            el: paginationElement,
            renderCustom: (swiper, current, total) => {
                return current + ' sur ' + total;
            },
            type: 'custom'
        },
        slidesPerView,
        spaceBetween: slidesPerViewMax === 1 ? 0 : 16
    });

    if (buttonPreviousElement && buttonNextElement && buttonPauseElement && buttonPlayElement) {
        // Click on previous button : stop autoplay
        buttonPreviousElement.addEventListener('click', () => {
            autoPlayStop(swiper, buttonPauseElement, buttonPlayElement);
        });

        // On click on next button : stop autoplay
        buttonNextElement.addEventListener('click', () => {
            autoPlayStop(swiper, buttonPauseElement, buttonPlayElement);
        });
    }

    if (buttonPauseElement && buttonPlayElement) {
        // Click on pause button : stop autoplay and set focus on play button
        buttonPauseElement.addEventListener('click', () => {
            autoPlayStop(swiper, buttonPauseElement, buttonPlayElement);
            buttonPlayElement.focus();
        });

        // Click on play button : start autoplay and set focus on pause button
        buttonPlayElement.addEventListener('click', () => {
            autoPlayStart(swiper, buttonPauseElement, buttonPlayElement);
            buttonPauseElement.focus();
        });
    }

    // Fix keyboard navigation using custom handler
    swiperKeyboardNavigationHandler(swiper);

    // When the active slide changes
    swiper.on('activeIndexChange', () => {
        // Update slides
        swiperUpdateSlides(swiper, swiper.activeIndex);
    });

    // Wait for the swiper to load and update slides
    // (Not great but the swiper.slides is empty otherwise and swiper events don't seem to work)
    setTimeout(() => {
        swiperUpdateSlides(swiper, swiper.activeIndex);
    }, 500);
}

function autoPlayStop (swiperInstance: Swiper, buttonPauseElement: HTMLElement, buttonPlayElement: HTMLElement): void {
    swiperInstance.autoplay.stop();
    buttonPlayElement.style.display = 'inherit';
    buttonPauseElement.style.display = 'none';
}

function autoPlayStart (swiperInstance: Swiper, buttonPauseElement: HTMLElement, buttonPlayElement: HTMLElement): void {
    swiperInstance.autoplay.start();
    buttonPauseElement.style.display = 'inherit';
    buttonPlayElement.style.display = 'none';
}